import { UNKNOWN_RATE_PLAN_ID, UNKNOWN_RATE_PLAN_NAME } from './egsRatePlans';
import { EGS_SLOTS } from './egsUtils';
import type { EgsGuestSlot } from './types';

export interface AppliedEgsSlot {
  cost: number;
  currency: string;
  /** A second record on the same room rate also prices this slot, so the price is ambiguous. */
  isConflicting: boolean;
  isPayableAtProperty: boolean;
  sell: number;
  slot: EgsGuestSlot;
}

export interface AppliedEgsRoomRate {
  ratePlanName: string;
  roomRateId: string;
  roomTypeName: string;
  slots: Array<AppliedEgsSlot>;
}

export interface AppliedEgsRatePlanGroup {
  ratePlanId: string;
  ratePlanName: string;
  roomRates: Array<AppliedEgsRoomRate>;
}

// All four slot pairs are stored on every record, so a zero cannot be told apart from a slot nobody set.
function isPriced(record: App.ExtraGuestSurcharges, slot: EgsGuestSlot) {
  return record[`${slot}_amount`] > 0 || record[`${slot}_cost`] > 0;
}

function buildSlots(records: Array<App.ExtraGuestSurcharges>): Array<AppliedEgsSlot> {
  return EGS_SLOTS.flatMap((slot) => {
    const pricing = records.filter((record) => isPriced(record, slot));

    // Every record pricing the slot is listed; picking a winner would hide the ambiguity behind one figure.
    return pricing.map((record) => ({
      cost: record[`${slot}_cost`],
      currency: record.currency,
      isConflicting: pricing.length > 1,
      isPayableAtProperty: !!record.payable_at_property,
      sell: record[`${slot}_amount`],
      slot,
    }));
  });
}

// Keyed on the rate plan id, because two plans can share a name.
export function buildAppliedEgsGroups({
  egsByRoomRateId,
  roomTypes,
}: {
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  roomTypes: Array<App.RoomType>;
}): Array<AppliedEgsRatePlanGroup> {
  const groups = new Map<string, AppliedEgsRatePlanGroup>();

  roomTypes.forEach((roomType) => {
    (roomType.room_rates ?? []).forEach((roomRate) => {
      const roomRateId = String(roomRate.id);
      const slots = buildSlots(egsByRoomRateId[roomRateId] ?? []);

      if (!slots.length) {
        return;
      }

      // The typing says `rate_plan` is always there; every sibling guards it anyway.
      const ratePlanId = roomRate.rate_plan?.id ?? UNKNOWN_RATE_PLAN_ID;
      const group = groups.get(ratePlanId) ?? {
        ratePlanId,
        ratePlanName: roomRate.rate_plan?.name ?? UNKNOWN_RATE_PLAN_NAME,
        roomRates: [],
      };

      group.roomRates.push({
        ratePlanName: group.ratePlanName,
        roomRateId,
        roomTypeName: roomType.name,
        slots,
      });
      groups.set(ratePlanId, group);
    });
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      roomRates: [...group.roomRates].sort((a, b) => a.roomTypeName.localeCompare(b.roomTypeName)),
    }))
    .sort((a, b) => a.ratePlanName.localeCompare(b.ratePlanName));
}

export function countAppliedEgsSlots(groups: Array<AppliedEgsRatePlanGroup>) {
  return groups.reduce(
    (total, group) => total + group.roomRates.reduce((subtotal, roomRate) => subtotal + roomRate.slots.length, 0),
    0,
  );
}

export function countAppliedEgsRoomRates(groups: Array<AppliedEgsRatePlanGroup>) {
  return groups.reduce((total, group) => total + group.roomRates.length, 0);
}
